//@ts-check

import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

// Material
import { makeStyles } from "@material-ui/core/styles"
import Popover from "@material-ui/core/Popover"
import classnames from "classnames"
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward"

import Button from "./button"
import { track } from "../utils/tracking"

/**
 * @param props {{
 * onViewSourceClick,
  filePath,
  highlightIndex,
  itemId,
  lines,
  anchorEl,
  handleClose,
 * }}
 */
const SnippetPopover = function ({
  onViewSourceClick,
  filePath,
  highlightIndex,
  itemId,
  lines,
  anchorEl,
  handleClose,
}) {
  const { t } = useTranslation()
  const styles = makeStyles(theme => ({
    codeTable: {
      borderCollapse: "collapse",
    },
    highlightedLine: {
      backgroundColor: "rgba(255, 0, 0, 0.2)",
    },
    codeFirstLine: {
      paddingTop: theme.spacing(1),
    },
    codeLastLine: {
      paddingBottom: theme.spacing(1),
    },
    popoverLineNumber: {
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
      fontWeight: "bold",
      textAlign: "end",
      backgroundColor: "#eee",
    },
    popoverSourceItem: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    popoverWrapper: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      maxHeight: 100,
      overflowY: "scroll",
    },
    showInEditorButton: {
      position: "absolute",
      zIndex: 100,
      right: theme.spacing(3),
      bottom: theme.spacing(1),
      backgroundColor: "#fff",
      "&:hover": {
        backgroundColor: "#eee",
      },
    },
    hiddenElement: {
      display: "none",
    },
  }))

  const classes = styles({})

  const [isPopoverHovered, setIsPopoverHovered] = React.useState(false)

  const [highlightLine, setHighlightLine] = useState(null)
  const [popoverWrapper, setPopoverWrapper] = useState(null)

  useEffect(() => {
    if (popoverWrapper && highlightLine){
      const startOfHighlightRow = highlightLine.offsetTop // scroll down to where highlight row starts => start of row at top of parent
      const halfParentHeight = popoverWrapper.offsetHeight / 2 // but need to scroll back up half of parent to put highlight in middle => start of row vertically-centered
      const halfHighlightHeight = highlightLine.offsetHeight / 2 // now take into account height of actual highlight row => make middle of row vertically-centered
      popoverWrapper.scrollTop = startOfHighlightRow - halfParentHeight + halfHighlightHeight
    }
  }, [highlightLine, popoverWrapper])

  return (<Popover
              open={anchorEl?.id === itemId}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            ><div
              className={classes.popoverWrapper}
              onMouseEnter={() => setIsPopoverHovered(true)}
              onMouseLeave={() => setIsPopoverHovered(false)}
              ref={popoverWrapper => setPopoverWrapper(popoverWrapper)}
            >
                <code><table className={classes.codeTable} >
                  {lines.map(([lineNumber, lineCode], arrayIndex) => {
                    const isFirstLine = arrayIndex === 0
                    const isLastLine = arrayIndex === lines.length - 1
                    const isHighlightedLine = lineNumber === highlightIndex
                    const lineClassNames = [isFirstLine && classes.codeFirstLine, isLastLine && classes.codeLastLine]
                    return (
                      <tr
                        className={classnames([isHighlightedLine && classes.highlightedLine])}
                        key={`${itemId}-code-line#${lineNumber}`}
                        ref={highlightLine => isHighlightedLine ? setHighlightLine(highlightLine) : null}
                      >
                        <td className={classnames([classes.popoverLineNumber, ...lineClassNames])}>{lineNumber}</td>
                        <td className={classnames([classes.popoverSourceItem, ...lineClassNames])} >{lineCode}</td>
                      </tr>
                    )
                  })}
                </table></code>
                <Button
                  variant="outlined"
                  className={classnames([classes.showInEditorButton, !isPopoverHovered && classes.hiddenElement])}
                  endIcon={<ArrowUpwardIcon />}
                  onClick={() => {
                    handleClose()
                    onViewSourceClick(highlightIndex, filePath)
                    track("Test highlighting show in editor", { filePath, lineNumber: highlightIndex })
                  }}
                >
                  {t("show_in_code_editor")}
                </Button>
              </div>
            </Popover>)

}

export default React.memo(SnippetPopover)